import { Injectable } from '@angular/core'
import {
	ICreatePlugin,
	IPlugin,
	IPublicView,
	ModelService,
  IMenuItem,
  IModuleWidget,
	PluginInjectorApplicationPlugin,
  PluginInjectorService
} from 'core-client/api'
import { validate as uuidValidate } from 'uuid';


@Injectable({
  providedIn: 'root'
})
export class PublicViewAuthService {
  /**
   * Configures the Angular OpenID Connect client
   */
  constructor(
    public pluginInjectorService: PluginInjectorService,
    private modelService: ModelService
  ) { }

  async accessGranted(uuid: string) {
    let granted = false;
    try {
      // check for valid UUID format
      if (uuidValidate(uuid)) {
        // find enabled public view configuration with given UUID
        const publicViews = await this.modelService._publicView.listAll(`enabled=true&uuid=${uuid}`);
        if (publicViews[0] != null) {
          const publicView: IPublicView = publicViews[0];
          // as we currently only support one widget at a time in a public view, there is just one source type provided
          if (publicView.source_type != null) {
            const sourceType = publicView.source_type.split('.');
            // number of substrings must be 3, because source_type is expected to have format '<pluginName>.<moduleName>.<widgetType>'
            if (sourceType.length === 3) {
              // plugin found and enabled in DB?
              return await this.loadRegisteredPlugin(sourceType[0].toUpperCase());
            }
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
    return granted;
  }

  private async loadRegisteredPlugin(name: string): Promise<boolean> {
    const registeredPlugin = this.pluginInjectorService.getPluginByName(name);
    if (registeredPlugin != null) {
      const installedPlugins = await this.modelService._plugin.listAll(`name=${name}`);
      let installedPlugin: IPlugin | {} | null = installedPlugins.length === 1 ? installedPlugins[0] : null;

      if (installedPlugin == null) {
        installedPlugin = await this.savePluginToDatabase(registeredPlugin);
      }

      if (installedPlugin != null && Object.keys(installedPlugin).length > 0) {
        this.updateRegisteredPluginInfos(registeredPlugin, installedPlugin as IPlugin);
        return registeredPlugin.enabled;
      }
    }
    return false;
  }

  private async savePluginToDatabase(plugin: PluginInjectorApplicationPlugin.IApplicationPlugin): Promise<IPlugin | {}> {
    let newPlugin = <ICreatePlugin>{}
    newPlugin.name = plugin.name
    newPlugin.enabled = plugin.enabled
    newPlugin.description = plugin.description

    if (plugin.settings) {
      newPlugin.settings = plugin.settings
    }

    if (plugin.permissions) {
      newPlugin.permissions = plugin.permissions
    }

    newPlugin.version = plugin.version
    return this.modelService._plugin.upsertOne(newPlugin);
  }

  private updateRegisteredPluginInfos(registeredPlugin: PluginInjectorApplicationPlugin.IApplicationPlugin, plugin: IPlugin): void {
    registeredPlugin._id = plugin._id;
    registeredPlugin.name = plugin.name;
    registeredPlugin.enabled = plugin.enabled;
    registeredPlugin.description = plugin.description;

    if (plugin.settings) {
      registeredPlugin.settings = plugin.settings
    }

    if (plugin.permissions) {
      registeredPlugin.permissions = plugin.permissions
    }

    registeredPlugin.version = plugin.version;
    registeredPlugin.installed_at = plugin.installed_at;

    if (registeredPlugin?.modules) {
      registeredPlugin.modules.forEach((pluginModule: PluginInjectorApplicationPlugin.IPluginModule) => {
        if (pluginModule.widgets) {
          pluginModule.widgets.forEach((widget: IModuleWidget) => {
            widget.pluginId = plugin._id
          })
        }
      })
    }

    if (registeredPlugin?.menuItems) {
      registeredPlugin.menuItems.forEach((item: IMenuItem) => {
        item.pluginId = plugin._id
      })
    } else {
      registeredPlugin.menuItems = []
    }
  }
}
